/**
 * Mercenary Mission Resolution Module
 *
 * Handles mission outcome calculation including:
 * - Success/failure determination
 * - Casualty calculation (permadeath)
 * - Reward generation
 */

import {
  MISSION_CONFIG,
  getRecommendedPower,
  calculateMissionSuccessRate,
} from '../../data/outposts/missions';
import { calculateSquadPower } from './stats';
import { DUNGEON_CONFIG, LOOT_TABLES } from '../../data/dungeon';

/**
 * Resolve a completed mission and determine outcome
 *
 * @param {Object} mission - Mission data object
 * @param {Array} mercenaries - Array of mercenary objects in the squad
 * @returns {Object} Mission result { success, casualties, survivors, rewards, xpGained }
 */
export function resolveMission(mission, mercenaries) {
  const { targetTier } = mission;

  // Calculate success chance
  const squadPower = calculateSquadPower(mercenaries);
  const successRate = calculateMissionSuccessRate(squadPower, targetTier);

  // Roll for success
  const success = Math.random() < successRate;

  // Initialize result
  const result = {
    success,
    casualties: [],         // Mercenary IDs that died
    survivors: [],          // Mercenary IDs that survived
    rewards: {
      experience: 0,        // XP for player
      mercXp: 0,            // XP for surviving mercs
      loot: [],             // Equipment/items dropped
      resources: {},        // Gold, materials, etc.
    },
    xpPerMerc: 0,
  };

  // Determine casualties (only on failure)
  mercenaries.forEach(merc => {
    if (!success && Math.random() < MISSION_CONFIG.deathChanceOnFailure) {
      result.casualties.push(merc.id);
    } else {
      result.survivors.push(merc.id);
    }
  });

  // Calculate rewards
  const tierConfig = DUNGEON_CONFIG.TIERS.find(t => t.id === targetTier);
  if (tierConfig) {
    const baseXp = tierConfig.expReward || 0;

    if (success) {
      // Full rewards on success (70% of manual)
      result.rewards.experience = Math.floor(baseXp * MISSION_CONFIG.rewardMultiplier * MISSION_CONFIG.playerXpMultiplier);
      result.rewards.mercXp = Math.floor(baseXp * MISSION_CONFIG.xpMultiplier);

      // Generate loot
      result.rewards.loot = generateMissionLoot(tierConfig.lootTable, 1);

      // Generate resources (gold, materials)
      result.rewards.resources = generateMissionResources(tierConfig);
    } else {
      // Partial rewards on failure (30%)
      result.rewards.experience = Math.floor(baseXp * MISSION_CONFIG.failureRewardMultiplier * MISSION_CONFIG.playerXpMultiplier);
      result.rewards.mercXp = Math.floor(baseXp * MISSION_CONFIG.failureRewardMultiplier * MISSION_CONFIG.xpMultiplier);

      // Reduced loot chance on failure
      if (Math.random() < 0.3) {
        result.rewards.loot = generateMissionLoot(tierConfig.lootTable, 0.5);
      }
    }

    // XP per surviving merc
    if (result.survivors.length > 0) {
      result.xpPerMerc = Math.floor(result.rewards.mercXp / result.survivors.length);
    }
  }

  return result;
}

/**
 * Generate loot drops from a mission
 * @param {string} lootTableName - Name of loot table (common, uncommon, rare, etc.)
 * @param {number} multiplier - Quantity multiplier
 * @returns {Array} Array of loot items
 */
function generateMissionLoot(lootTableName, multiplier = 1) {
  const loot = [];
  const lootTable = LOOT_TABLES[lootTableName];

  if (!lootTable) {
    return loot;
  }

  // Roll for equipment drop
  if (Math.random() < lootTable.equipment * multiplier) {
    // Generate random equipment
    const rarity = rollRarity(lootTable.rarityWeights);
    loot.push({
      type: 'equipment',
      rarity,
      // Actual equipment will be generated when collected
    });
  }

  // Roll for gold
  if (Math.random() < lootTable.gold * multiplier) {
    const goldAmount = rollGoldAmount(lootTableName);
    loot.push({
      type: 'gold',
      amount: Math.floor(goldAmount * multiplier),
    });
  }

  // Roll for resources/materials
  if (Math.random() < lootTable.resources * multiplier) {
    const materials = rollMaterials(lootTableName);
    materials.forEach(mat => {
      loot.push({
        type: 'material',
        ...mat,
      });
    });
  }

  return loot;
}

/**
 * Roll for equipment rarity based on weights
 * @param {Object} rarityWeights - Weights for each rarity
 * @returns {string} Rarity name
 */
function rollRarity(rarityWeights) {
  const totalWeight = Object.values(rarityWeights).reduce((a, b) => a + b, 0);
  let roll = Math.random() * totalWeight;

  for (const [rarity, weight] of Object.entries(rarityWeights)) {
    roll -= weight;
    if (roll <= 0) {
      return rarity;
    }
  }

  return 'common';
}

/**
 * Roll gold amount based on loot table tier
 * @param {string} lootTableName - Loot table name
 * @returns {number} Gold amount
 */
function rollGoldAmount(lootTableName) {
  const goldRanges = {
    common: [10, 30],
    uncommon: [25, 60],
    rare: [50, 120],
    epic: [100, 250],
    legendary: [200, 500],
    mythic: [400, 1000],
    boss: [500, 1500],
  };

  const [min, max] = goldRanges[lootTableName] || [10, 30];
  return Math.floor(min + Math.random() * (max - min));
}

/**
 * Roll for material drops
 * @param {string} lootTableName - Loot table name
 * @returns {Array} Array of { item, quantity }
 */
function rollMaterials(lootTableName) {
  const materials = [];

  // Material pools by tier
  const materialPools = {
    common: ['bone', 'leather', 'cloth'],
    uncommon: ['bone', 'leather', 'cloth', 'ironOre'],
    rare: ['leather', 'ironOre', 'essence'],
    epic: ['essence', 'crystal', 'darkSteel'],
    legendary: ['soulShard', 'voidFragment', 'starEssence'],
    mythic: ['cosmicOre', 'eterniumIngot', 'starEssence'],
  };

  const pool = materialPools[lootTableName] || materialPools.common;
  const numMaterials = Math.floor(1 + Math.random() * 2); // 1-2 materials

  for (let i = 0; i < numMaterials; i++) {
    const item = pool[Math.floor(Math.random() * pool.length)];
    const quantity = Math.floor(1 + Math.random() * 3); // 1-3 quantity

    materials.push({ item, quantity });
  }

  return materials;
}

/**
 * Generate resource rewards (gold, star essence, gemstones)
 * @param {Object} tierConfig - Dungeon tier configuration
 * @returns {Object} Resource amounts { gold, starEssence, etc. }
 */
function generateMissionResources(tierConfig) {
  const resources = {};
  const tier = parseInt(tierConfig.tier) || 1;

  // Gold scales with tier
  resources.gold = Math.floor((50 + tier * 30) * (0.8 + Math.random() * 0.4));

  // Star essence (higher tiers)
  if (tier >= 2 && Math.random() < 0.3 + tier * 0.1) {
    resources.starEssence = Math.floor(tier * 2 + Math.random() * tier * 3);
  }

  // Gemstones (random)
  const gemstones = ['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'];
  if (Math.random() < 0.2 + tier * 0.05) {
    const gem = gemstones[Math.floor(Math.random() * gemstones.length)];
    resources[gem] = Math.floor(1 + Math.random() * (tier * 0.5));
  }

  return resources;
}

/**
 * Preview mission rewards (for UI display before starting)
 * @param {string} tierId - Target dungeon tier
 * @param {number} squadPower - Current squad power
 * @returns {Object} Preview data { successRate, duration, estimatedXp, deathRisk }
 */
export function previewMission(tierId, squadPower) {
  const tierConfig = DUNGEON_CONFIG.TIERS.find(t => t.id === tierId);
  const successRate = calculateMissionSuccessRate(squadPower, tierId);

  // Duration calculation
  const tierMatch = tierId.match(/tier(\d)/);
  const tierNum = tierMatch ? parseInt(tierMatch[1]) : 1;
  const multiplier = MISSION_CONFIG.tierDurationMultipliers[`tier${tierNum}`] || 1.0;
  const duration = Math.floor(MISSION_CONFIG.baseDuration * multiplier);

  // Estimated XP
  const baseXp = tierConfig?.expReward || 0;
  const estimatedXp = Math.floor(baseXp * MISSION_CONFIG.rewardMultiplier);

  // Death risk (only applies on failure)
  const failureChance = 1 - successRate;
  const deathRisk = failureChance * MISSION_CONFIG.deathChanceOnFailure;

  return {
    successRate,
    duration,
    estimatedXp,
    deathRisk,
    tierName: tierConfig?.name || 'Unknown',
    tierIcon: tierConfig?.icon || '?',
    lootTable: tierConfig?.lootTable || 'common',
  };
}
